/** Stati fattura già trasmesse / in ciclo SDI. */
export const SDI_STATI = new Set(["inviata_sdi", "consegnata", "accettata", "rifiutata", "scartata"]);

/** True se la fattura è in coda di invio SDI in background (flag `sdi_coda_at`). */
export function fatturaInCodaSdi(f) {
  return Boolean(f && f.sdi_coda_at);
}

export const SDI_STATO_BADGE = {
  consegnata: { label: "Consegnata", className: "bg-emerald-100 text-emerald-800" },
  accettata: { label: "Accettata", className: "bg-emerald-100 text-emerald-800" },
  inviata_sdi: { label: "Inviata / in consegna", className: "bg-amber-100 text-amber-800" },
  scartata: { label: "Scartata", className: "bg-red-100 text-red-800" },
  rifiutata: { label: "Rifiutata", className: "bg-red-100 text-red-800" },
  stornata: { label: "Stornata", className: "bg-purple-100 text-purple-800" },
};

export const SDI_FILTRO_STATO = [
  { value: "__tutti__", label: "Tutti gli stati" },
  { value: "consegnata", label: "Consegnate / accettate" },
  { value: "in_attesa", label: "Inviate / in consegna" },
  { value: "scartata", label: "Scartate / rifiutate" },
];

export function sdiStatoGruppo(stato) {
  if (["consegnata", "accettata"].includes(stato)) return "consegnata";
  if (["scartata", "rifiutata"].includes(stato)) return "scartata";
  if (stato === "inviata_sdi") return "in_attesa";
  return null;
}

export function matchSdiFiltroStato(f, filtro) {
  if (!filtro || filtro === "__tutti__") return true;
  return sdiStatoGruppo(f.stato) === filtro;
}

const FIC_EI_STATUS_IT = {
  attempt: "Invio in corso",
  missing: "Documento mancante",
  not_sent: "Non inviata SDI",
  sent: "Inviata allo SDI",
  pending: "In lavorazione SDI",
  processing: "In consegna SDI",
  error: "Errore invio",
  discarded: "Scartata dallo SDI",
  not_delivered: "Non consegnata",
  delivered: "Recapitata",
  accepted: "Accettata",
  manual_accepted: "Accettata",
  rejected: "Rifiutata",
  manual_rejected: "Rifiutata",
  no_response: "Recapitata (silenzio assenso)",
};

/** Etichetta italiana per ei_status grezzo FIC (popup, tab Fatture attive). */
export function ficEiStatusLabel(eiStatus) {
  const ei = String(eiStatus || "").trim().toLowerCase();
  if (!ei) return null;
  return FIC_EI_STATUS_IT[ei] || ei.replace(/_/g, " ");
}

const FIC_EI_STATUS_ELIMINABILE = new Set(["not_sent", "missing", ""]);

/** True se il documento FIC non è ancora stato inviato allo SDI (eliminabile). */
export function ficEiStatusEliminabile(eiStatus) {
  const ei = String(eiStatus || "").trim().toLowerCase();
  return FIC_EI_STATUS_ELIMINABILE.has(ei);
}

/** Fattura Affidya modificabile (voci, ricalcolo) — non inviata SDI o stati editabili. */
export function fatturaModificabile(f) {
  if (!f) return false;
  const stOk = ["bozza", "generata", "approvata", "rifiutata", "scartata"].includes(f.stato);
  return stOk && !(Number(f.importo_pagato) > 0);
}

/** Fattura Affidya eliminabile (standard o via FIC non inviata SDI). */
export function fatturaEliminabile(f, { isFic = false } = {}) {
  if (!f) return false;
  if (Number(f.importo_pagato) > 0) return false;
  const stOk = ["bozza", "generata", "approvata", "rifiutata", "scartata"].includes(f.stato);
  if (stOk) return true;
  if (isFic && (f.provider_id || f.fic_document_id)) {
    return ficEiStatusEliminabile(f.sdi_ei_status);
  }
  return false;
}

/** Eliminazione coordinata Affidya + FIC consigliata. */
export function fatturaEliminaSuFic(f, isFic) {
  return !!(
    isFic &&
    (f?.provider_id || f?.fic_document_id) &&
    ficEiStatusEliminabile(f?.sdi_ei_status)
  );
}

export function sdiEsitoLabel(f) {
  const syncErr = String(f?.sdi_ultimo_sync_errore || "").trim();
  if (syncErr && sdiStatoGruppo(f.stato) === "in_attesa") {
    if (isFicUsageLimitSyncError(syncErr)) {
      // Limite API FIC: non è uno scarto SDI — mostra lo stato reale.
      return "Inviata allo SDI — in attesa di consegna";
    }
    return syncErr.length > 80 ? `${syncErr.slice(0, 77)}…` : syncErr;
  }

  const ei = String(f?.sdi_ei_status || "").trim().toLowerCase();
  if (ei && FIC_EI_STATUS_IT[ei]) return FIC_EI_STATUS_IT[ei];

  const g = sdiStatoGruppo(f.stato);
  if (g === "consegnata") return f.stato === "accettata" ? "Accettata" : "Recapitata";
  if (g === "in_attesa") return "Inviata allo SDI — in attesa di consegna";
  if (g === "scartata") {
    const msg = f.ultimo_errore_sdi_leggibile || f.ultimo_errore_sdi || f.sdi_ultimo_sync_errore;
    return msg ? `Scarto: ${msg}` : f.stato === "rifiutata" ? "Rifiutata" : "Scartata";
  }
  return "—";
}

/** Messaggio utente quando FIC risponde USAGE_LIMIT_REACHED (quota API). */
export const FIC_USAGE_LIMIT_MESSAGE =
  "Limite chiamate API di Fatture in Cloud raggiunto. Lo stato SDI non è aggiornabile al momento: riprova più tardi (il piano FIC ha un tetto orario/mensile di richieste).";

const SDI_USAGE_LIMIT_STORAGE_KEY = "affidya.fatturazione.sdiUsageLimit";

/** Errore di sync dovuto a quota API Fatture in Cloud (non a esito SDI). */
export function isFicUsageLimitSyncError(msg) {
  const s = String(msg || "")
    .replace(/&quot;/gi, '"')
    .replace(/&#34;/g, '"')
    .toLowerCase();
  return (
    s.includes("usage_limit_reached") ||
    s.includes("api usage limit") ||
    s.includes("limite chiamate api di fatture in cloud")
  );
}

/** Legge il banner quota dalla sessione (sopravvive al cambio tab). */
export function readSdiUsageLimitBanner() {
  try {
    const raw = sessionStorage.getItem(SDI_USAGE_LIMIT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const message = String(parsed?.message || "").trim();
    if (!message) return null;
    return {
      message,
      at: parsed?.at || null,
    };
  } catch {
    return null;
  }
}

export function writeSdiUsageLimitBanner(message) {
  const payload = {
    message: String(message || FIC_USAGE_LIMIT_MESSAGE).trim() || FIC_USAGE_LIMIT_MESSAGE,
    at: new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(SDI_USAGE_LIMIT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
  return payload;
}

export function clearSdiUsageLimitBanner() {
  try {
    sessionStorage.removeItem(SDI_USAGE_LIMIT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function sdiDataConsegna(f) {
  if (!["consegnata", "accettata"].includes(f.stato)) return null;
  return f.sdi_consegna_at || f.updatedAt || null;
}

export function computeSdiStats(fattureInAnno) {
  const list = fattureInAnno.filter((f) => SDI_STATI.has(f.stato));
  const totale = list.length;
  const consegnate = list.filter((f) => sdiStatoGruppo(f.stato) === "consegnata");
  const inAttesa = list.filter((f) => sdiStatoGruppo(f.stato) === "in_attesa");
  const scartate = list.filter((f) => sdiStatoGruppo(f.stato) === "scartata");
  const importoTotale = list.reduce((a, f) => a + (f.totale || 0), 0);
  const pct = (n) => (totale > 0 ? Math.round((n / totale) * 1000) / 10 : 0);

  const daVerificare = list.filter(
    (f) =>
      sdiStatoGruppo(f.stato) === "in_attesa" &&
      (f.ultimo_errore_sdi || f.ultimo_errore_sdi_leggibile ||
        (f.sdi_ultimo_sync_errore && !isFicUsageLimitSyncError(f.sdi_ultimo_sync_errore))),
  ).length;

  return {
    totale,
    consegnate: { count: consegnate.length, pct: pct(consegnate.length) },
    inAttesa: { count: inAttesa.length, pct: pct(inAttesa.length) },
    scartate: { count: scartate.length, pct: pct(scartate.length) },
    daVerificare: { count: daVerificare, pct: pct(daVerificare) },
    importoTotale,
  };
}

export function filterFattureSdi(list, { searchQuery, filtroStato, filtroMese, dataDa, dataA, resolveOspite }) {
  let out = list.filter((f) => SDI_STATI.has(f.stato));
  out = out.filter((f) => matchSdiFiltroStato(f, filtroStato));

  if (filtroMese && filtroMese !== "__tutti__") {
    out = out.filter((f) => {
      const ref = f.sdi_invio_at || f.data_emissione || (f.mese ? `${f.mese}-01` : "");
      return String(ref).slice(0, 7) === filtroMese;
    });
  }

  if (dataDa) {
    out = out.filter((f) => {
      const ref = (f.sdi_invio_at || f.data_emissione || "").slice(0, 10);
      return ref && ref >= dataDa;
    });
  }
  if (dataA) {
    out = out.filter((f) => {
      const ref = (f.sdi_invio_at || f.data_emissione || "").slice(0, 10);
      return ref && ref <= dataA;
    });
  }

  const q = (searchQuery || "").trim().toLowerCase();
  if (q) {
    out = out.filter((f) => {
      const ospite = String(resolveOspite?.(f) || "").toLowerCase();
      return (
        (f.numero || "").toLowerCase().includes(q) ||
        (f.destinatario_nome || "").toLowerCase().includes(q) ||
        (f.destinatario_cf || "").toLowerCase().includes(q) ||
        (f.destinatario_piva || "").toLowerCase().includes(q) ||
        ospite.includes(q)
      );
    });
  }

  return out.sort((a, b) => {
    const da = a.sdi_invio_at || a.data_emissione || "";
    const db = b.sdi_invio_at || b.data_emissione || "";
    return String(db).localeCompare(String(da));
  });
}
